import { Button, Card, Col, Collapse, CollapseProps, GetProp, Popconfirm, Row, Space, Tag, Typography, theme } from 'antd';
import { FC, useState } from 'react';
import { ExclamationCircle, PencilSquare, PlusLg, Trash } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { AppRoutes, BaseQueryParams } from '@/common/interfaces';

import { useGetByIdPageNodeQuery } from '@/modules/Pages';

import { CollapseNodesList, CollapsePagesList } from './Lists';
import { PageNodesBeadcrumb } from './PageNodesBeadcrumb';
import './PagesNodeOverview.scss';

interface Props {
  siteId: string;
  pageNode: BaseQueryParams<typeof useGetByIdPageNodeQuery>['data'];
  isEditingCurrentPageNode: boolean;
  disableControlButtons: boolean;
  isDeleteButtonLoading: boolean;
  isPageNodeLoading: boolean;
  handleDeleteNode: () => void;
}

enum CollapseListKeys {
  ChildrenCollapseList = 'childrenCollapseList',
  PagesCollapseList = 'pagesCollapseList'
}

export const PageNodesOverviewView: FC<Props> = ({
  siteId,
  pageNode,
  isEditingCurrentPageNode,
  isDeleteButtonLoading,
  disableControlButtons,
  isPageNodeLoading,
  handleDeleteNode
}) => {
  const { colorBorder, red5, colorError } = theme.useToken().token;

  const nodeHasChildren = pageNode.children.length > 0;
  const nodeHasPages = pageNode.pages.length > 0;

  const [collapseListVisibleKeys, setCollapseListVisibleKeys] = useState<CollapseListKeys[]>([
    CollapseListKeys.ChildrenCollapseList,
    CollapseListKeys.PagesCollapseList
  ]);

  const handleChangeCollapseListVisibleKeys = (key: string | string[]): void => {
    setCollapseListVisibleKeys((Array.isArray(key) ? key : [key]) as unknown as CollapseListKeys[]);
  };

  return (
    <>
      <Row gutter={ [16, 16] }>
        <Col span={ 24 }>
          <Card size="small">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <PageNodesBeadcrumb
                pageNodeBreadcrumb={ pageNode.breadcrumb }
                pageNodeId={ pageNode.id }
                pageNodeSiteId={ siteId }
              />

              <Space.Compact>
                <Link to={ AppRoutes.PageNodesByIdEdit.replace(':siteId', siteId).replace(':nodeId', pageNode.id) }>
                  <Button
                    disabled={ disableControlButtons }
                    icon={ <PencilSquare size={ 16 }/> }
                    type={ isEditingCurrentPageNode ? 'primary' : 'default' }
                  />
                </Link>
                <Popconfirm
                  overlayStyle={{ maxWidth: '350px' }}
                  destroyTooltipOnHide
                  title="Видалити ноду?"
                  icon={ <ExclamationCircle color={ colorError }/> }
                  description={ 'Будуть видалені всі внутрішні ноди та сторінки, які їй належать' }
                  onConfirm={ handleDeleteNode }
                  okText="Так, видалити"
                  okButtonProps={{ danger: true }}
                  cancelText="Ні"
                  placement="left"
                  disabled={ disableControlButtons }
                >
                  <Button
                    disabled={ disableControlButtons || pageNode.isHome }
                    loading={ isDeleteButtonLoading }
                    icon={ <Trash size={ 16 } color={ red5 }/> }
                  />
                </Popconfirm>
              </Space.Compact>
            </div>
          </Card>
        </Col>

        <Col span={ 24 }>
          <Collapse
            onChange={ handleChangeCollapseListVisibleKeys }
            className="page-node-collapse-lists"
            activeKey={ collapseListVisibleKeys }
            expandIconPosition="end"
            size="small"
            items={ collapseItems({
              nodeHasChildren,
              nodeHasPages,
              pageNode,
              siteId,
              disableControlButtons,
              isPageNodeLoading,
              colorBorder
            }) }
            // expandIcon={ (properties): ReactNode => (
            //   <CaretRightFill
            //     size={ 16 }
            //     style={{ transition: '300ms', transform: `rotate(${ properties.isActive ? 90 : 0 }deg)` }}
            //   />
            // ) }
          />
        </Col>
      </Row>
    </>
  );
};

const collapseItems = (args: {
  nodeHasChildren: boolean;
  nodeHasPages: boolean;
  pageNode: Props['pageNode'];
  siteId: string;
  disableControlButtons: boolean;
  isPageNodeLoading: boolean;
  colorBorder: string;
}): GetProp<CollapseProps, 'items'> => {
  const { nodeHasChildren, nodeHasPages, pageNode, siteId, disableControlButtons, isPageNodeLoading, colorBorder } = args;

  return [
    {
      showArrow: false,
      forceRender: nodeHasChildren,
      collapsible: 'icon',
      style: { border: `1px solid ${ colorBorder }` },
      extra: (
        <Space>
          <Link
            to={ AppRoutes.PageNodesCreate.replace(':siteId', siteId).replace(':nodeId', pageNode.id) }
            state={{ parentNodeId: pageNode.id }}
          >
            <Button type="link" size="small" icon={ <PlusLg/> } disabled={ disableControlButtons }/>
          </Link>
          { nodeHasChildren ? <Tag color="blue">{ pageNode.children.length }</Tag> : <Tag color="red">Відсутні</Tag> }
        </Space>
      ),
      label: (
        <Typography.Title level={ 5 } style={{ margin: 0 }}>
          Внутрішні ноди
        </Typography.Title>
      ),
      key: CollapseListKeys.ChildrenCollapseList,
      children: (
        <CollapseNodesList
          loading={ isPageNodeLoading }
          pageNodeChildren={ pageNode.children }
          siteId={ siteId }
          disableControlButtons={ disableControlButtons }
        />
      )
    }, {
      showArrow: false,
      forceRender: nodeHasPages,
      collapsible: 'icon',
      style: { border: `1px solid ${ colorBorder }` },
      extra: (
        <Space>
          <Link to={ AppRoutes.PagesCreate.replace(':siteId', siteId).replace(':nodeId', pageNode.id) }>
            <Button type="link" size="small" icon={ <PlusLg/> } disabled={ disableControlButtons }/>
          </Link>
          { nodeHasPages ? <Tag color="blue">{ pageNode.pages.length }</Tag> : <Tag color="red">Відсутні</Tag> }
        </Space>
      ),
      label: (
        <Typography.Title level={ 5 } style={{ margin: 0 }}>
          Сторінки ноди
        </Typography.Title>
      ),
      key: CollapseListKeys.PagesCollapseList,
      children: (
        <CollapsePagesList
          loading={ isPageNodeLoading }
          pageNodePages={ pageNode.pages }
          siteId={ siteId }
        />
      )
    }
  ];
};

// переписати на https://codesandbox.io/s/basic-antd-5-5-0-forked-0ujy2x?file=/demo.tsx
